import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useResultModal } from '@/hooks/useResultModal';
import Cropper, { Area } from 'react-easy-crop';
import styled from '@emotion/styled';
import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

/**
 * 16:9 고정 비율 이미지 크롭 모달.
 *
 * 사용자가 선택한 이미지를 줌/드래그로 조정한 뒤 "적용"을 누르면
 * canvas 로 1280x720(16:9) 크기의 JPEG File 을 만들어 onCropped 로 전달한다.
 *
 * @prop file 크롭할 원본 파일 (null 이면 모달 닫힘)
 * @prop aspect 크롭 비율 (기본 16/9)
 * @prop onCropped 크롭 완료 시 결과 File 콜백
 * @prop onCancel 취소/닫기 콜백
 */
interface ImageCropModalProps {
    file: File | null;
    aspect?: number;
    outputWidth?: number;
    outputHeight?: number;
    onCropped: (croppedFile: File) => void;
    onCancel: () => void;
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
`;

const ModalBox = styled.div`
    background: #fff;
    border-radius: 12px;
    width: 100%;
    max-width: 560px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
    padding: 16px 20px;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1a1a1a;
    border-bottom: 1px solid #f0f0f0;
`;

const CropArea = styled.div`
    position: relative;
    width: 100%;
    height: 320px;
    background: #1a1a1a;
    /* 폰에서 끌면 페이지가 아니라 사진이 움직이게 */
    touch-action: none;
    overscroll-behavior: contain;

    @media (max-width: 768px) {
        height: 240px;
    }
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px 4px;
`;

const ZoomLabel = styled.span`
    font-size: 0.85rem;
    color: #6b7280;
    white-space: nowrap;
`;

const ZoomSlider = styled.input`
    flex: 1;
    accent-color: ${designSystem.colors.primary};
`;

const WarnText = styled.p`
    font-size: 0.8rem;
    color: #d97706;
    margin: 8px 20px 0;
`;

const Footer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 16px 20px;
`;

const CancelBtn = styled.button`
    padding: 10px 20px;
    background: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;

    &:hover {
        background: #e5e7eb;
    }
`;

const ApplyBtn = styled.button`
    padding: 10px 24px;
    background: ${designSystem.colors.primary};
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;

    &:hover {
        background: ${designSystem.colors.primaryDark};
    }

    &:disabled {
        background: #e2e8f0;
        color: #94a3b8;
        cursor: not-allowed;
    }
`;

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (err) => reject(err));
        image.src = url;
    });

/**
 * 원본 이미지에서 croppedAreaPixels 영역을 잘라 outputWidth x outputHeight 크기의 JPEG File 로 반환한다.
 */
const getCroppedImg = async (
    imageSrc: string,
    croppedAreaPixels: Area,
    outputWidth: number,
    outputHeight: number,
    fileName: string,
): Promise<File> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas context not available');
    }

    ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        outputWidth,
        outputHeight,
    );

    return new Promise<File>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Failed to create blob'));
                    return;
                }
                const baseName: string = fileName.replace(/\.[^.]+$/, '');
                resolve(new File([blob], `${baseName}_16x9.jpg`, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.92,
        );
    });
};

const ImageCropModal = ({
    file,
    aspect = 16 / 9,
    outputWidth = 1280,
    outputHeight = 720,
    onCropped,
    onCancel,
}: ImageCropModalProps) => {
    const { showErrorMessage, resultModal } = useResultModal();
    const [imageSrc, setImageSrc] = useState<string>('');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [lowRes, setLowRes] = useState(false);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (!file) {
            setImageSrc('');
            return;
        }
        // 모달 진입 시 상태 초기화
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setLowRes(false);
        const url: string = URL.createObjectURL(file);
        setImageSrc(url);
        // 최소 권장 해상도(1280x720) 미만 경고용 체크
        createImage(url)
            .then((img) => {
                setLowRes(img.naturalWidth < outputWidth || img.naturalHeight < outputHeight);
            })
            .catch(() => setLowRes(false));
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [file, outputWidth, outputHeight]);

    const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const handleApply = async () => {
        if (!file || !croppedAreaPixels) return;
        setProcessing(true);
        try {
            const cropped: File = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                outputWidth,
                outputHeight,
                file.name,
            );
            onCropped(cropped);
        } catch (error) {
            console.error('이미지 크롭 실패:', error);
            showErrorMessage('이미지 크롭 중 오류가 발생했습니다.');
        } finally {
            setProcessing(false);
        }
    };

    // 열려 있는 동안 뒤 페이지가 스크롤되지 않게(폰에서 사진을 끌 때 페이지가 딸려 움직이던 문제)
    useEffect(() => {
        if (!imageSrc) return undefined;
        const { body, documentElement } = document;
        const prevBody: string = body.style.overflow;
        const prevHtml: string = documentElement.style.overflow;
        body.style.overflow = 'hidden';
        documentElement.style.overflow = 'hidden';
        return () => {
            body.style.overflow = prevBody;
            documentElement.style.overflow = prevHtml;
        };
    }, [imageSrc]);

    if (!file || !imageSrc) return null;

    // 본문(당겨서 새로고침·스와이프 뒤로가기로 transform 되는 곳) 밖, body 에 바로 띄워야 화면에 고정된다
    return createPortal(
        <Overlay onClick={onCancel} data-no-swipe-back>
            {resultModal}
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <Header>이미지 크롭 (16:9)</Header>
                <CropArea>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        objectFit="contain"
                    />
                </CropArea>
                <Controls>
                    <ZoomLabel>확대</ZoomLabel>
                    <ZoomSlider
                        type="range"
                        min={1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                    />
                </Controls>
                {lowRes && (
                    <WarnText>
                        권장 해상도({outputWidth}×{outputHeight})보다 작은 이미지입니다. 화질이 다소 떨어질 수 있어요.
                    </WarnText>
                )}
                <Footer>
                    <CancelBtn type="button" onClick={onCancel}>
                        취소
                    </CancelBtn>
                    <ApplyBtn type="button" onClick={handleApply} disabled={processing || !croppedAreaPixels}>
                        {processing ? <ButtonSpinner /> : '적용'}
                    </ApplyBtn>
                </Footer>
            </ModalBox>
        </Overlay>,
        document.body,
    );
};

export default ImageCropModal;
